// src/pages/LearningPathsPage.tsx — /paths: my learning paths + the
// conversational intake (docs/learning-paths/v1-spec.md §UX pt 1; chat
// replaced the form on Francis's call 2026-08-11 — many code learners can't
// compress a goal into one prompt, so the counselor narrows it down and the
// user confirms a proposal before the 30s generation runs).
//
// Progressive layout: with zero paths the intake takes center stage; once
// paths exist it moves left with the card grid beside it, 50:50. The card
// list renders instantly from pathsStore on revisit (stale-while-revalidate
// against the API, which is itself Redis-cached server-side).
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import OnboardingModal from '../components/OnboardingModal';
import { onboardingApi, pathsApi } from '../services/api';
import { PathProposal, PathSummary } from '../types/paths';
import { usePathsStore } from '../stores/pathsStore';
import PathIntakeChat from '../paths/PathIntakeChat';

const formatTotal = (min: number): string => {
  if (min < 60) return `${min} min`;
  const h = Math.round(min / 60);
  return `~${h} hr${h > 1 ? 's' : ''}`;
};

const PathCard: React.FC<{ path: PathSummary; onOpen: () => void }> = ({ path, onOpen }) => (
  <button
    onClick={onOpen}
    className="text-left bg-card border border-border rounded-2xl p-5 hover:border-purple-400 hover:shadow-lg transition-all"
  >
    <div className="text-sm font-semibold text-foreground line-clamp-2">{path.topic}</div>
    <div className="flex flex-wrap gap-2 mt-3 text-xs text-muted-foreground">
      <span className="px-2 py-1 rounded-lg bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 font-medium">
        {path.duration_hours}h target
      </span>
      <span className="px-2 py-1 rounded-lg bg-background border border-border">
        {path.node_count} steps
      </span>
      <span className="px-2 py-1 rounded-lg bg-background border border-border">
        {formatTotal(path.total_est_minutes)}
      </span>
    </div>
    <div className="text-xs text-muted-foreground mt-3">
      Created {new Date(path.created_at).toLocaleDateString()}
    </div>
  </button>
);

const SkeletonCard: React.FC = () => (
  <div className="bg-card border border-border rounded-2xl p-5 animate-pulse">
    <div className="h-4 bg-muted rounded w-3/4 mb-4"></div>
    <div className="flex gap-2">
      <div className="h-6 bg-muted rounded-lg w-16"></div>
      <div className="h-6 bg-muted rounded-lg w-14"></div>
      <div className="h-6 bg-muted rounded-lg w-14"></div>
    </div>
    <div className="h-3 bg-muted rounded w-1/3 mt-4"></div>
  </div>
);

const LearningPathsPage: React.FC = () => {
  const navigate = useNavigate();
  const { summaries, setSummaries, prependSummary } = usePathsStore();

  const [building, setBuilding] = useState(false);
  const [buildError, setBuildError] = useState<string | null>(null);
  // One-time re-prompt for onboarding skippers (docs/onboarding/v1-spec.md):
  // /paths is where the profile has visible payoff, so skippers get exactly
  // one second chance here. localStorage flag set the moment it shows —
  // closing without answering still counts as the one chance.
  const [showOnboardingReprompt, setShowOnboardingReprompt] = useState(false);
  useEffect(() => {
    if (localStorage.getItem('onboarding_paths_reprompted')) return;
    onboardingApi.getStatus()
      .then((s) => {
        if (s.onboarding_status === 'skipped') {
          localStorage.setItem('onboarding_paths_reprompted', 'true');
          setShowOnboardingReprompt(true);
        }
      })
      .catch(() => { /* fail open */ });
  }, []);

  // Stale-while-revalidate: cached summaries render immediately; the fetch
  // below refreshes them silently. First-ever visit shows skeletons instead.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await pathsApi.listPaths();
        if (!cancelled) setSummaries(res.paths);
      } catch (e) {
        console.error('[PATHS] Failed to load paths:', e);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBuild = async (proposal: PathProposal) => {
    setBuilding(true);
    setBuildError(null);
    try {
      const path = await pathsApi.createPath(
        proposal.topic,
        proposal.duration_hours,
        proposal.already_know || undefined
      );
      prependSummary({
        path_id: path.path_id,
        topic: path.topic,
        duration_hours: path.duration_hours,
        node_count: path.nodes.length,
        total_est_minutes: path.nodes.reduce((a, n) => a + n.est_minutes, 0),
        created_at: path.created_at,
      });
      navigate(`/paths/${path.path_id}`);
    } catch (err: any) {
      console.error('[PATHS] Generation failed:', err);
      setBuildError(
        err?.response?.data?.detail ||
          'Path generation failed — please try again in a moment.'
      );
      setBuilding(false);
    }
  };

  const isFirstLoad = summaries === null;
  const hasPaths = (summaries?.length ?? 0) > 0;
  const sideBySide = hasPaths || isFirstLoad;

  const intakePane = (
    <div className="space-y-3">
      <PathIntakeChat building={building} onBuild={handleBuild} />
      {buildError && (
        <div className="px-4 py-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">
          {buildError}
        </div>
      )}
    </div>
  );

  return (
    <div className="h-screen flex overflow-hidden">
      {showOnboardingReprompt && (
        <OnboardingModal mode="questions" onClose={() => setShowOnboardingReprompt(false)} />
      )}
      <Sidebar />
      <div className="flex-1 flex flex-col min-h-screen bg-background overflow-auto">
        <header className="border-b border-border bg-card">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center space-x-3">
                <img src="/cubemate-logo.png" alt="Cubemate Logo" className="w-8 h-8 rounded-lg" />
                <div>
                  <h1 className="text-lg font-semibold text-foreground">Learning Paths</h1>
                  <span className="text-xs text-muted-foreground">
                    Your personal roadmap — every step opens in Chat, Bookmate or Capstone
                  </span>
                </div>
              </div>
            </div>
          </div>
        </header>

        <div className="max-w-6xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8">
          {sideBySide ? (
            /* Intake left, paths right — 50:50, both visible without scrolling */
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
              <div>{intakePane}</div>
              <div>
                <h2 className="text-base font-semibold text-foreground mb-4">My paths</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {isFirstLoad ? (
                    <>
                      <SkeletonCard />
                      <SkeletonCard />
                      <SkeletonCard />
                    </>
                  ) : (
                    summaries!.map((p) => (
                      <PathCard
                        key={p.path_id}
                        path={p}
                        onOpen={() => navigate(`/paths/${p.path_id}`)}
                      />
                    ))
                  )}
                </div>
              </div>
            </div>
          ) : (
            /* No paths yet — the intake takes center stage */
            <div className="max-w-2xl mx-auto space-y-6">
              {intakePane}
              <p className="text-sm text-muted-foreground text-center">
                🗺️ Your paths will appear here after you build one — a step-by-step graph
                you can revisit any time.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LearningPathsPage;
